/**
 * Translations for i18n support
 *
 * Structure:
 * - COMMON: Shared text across multiple components
 * - PAGE_*: Page-specific translations
 * - COMPONENT_*: Component-specific translations
 */

import { Language } from '@/contexts/SettingsContext';

// =============================================================================
// COMMON TRANSLATIONS
// =============================================================================

export const COMMON_TEXT = {
  ko: {
    // Buttons
    submit: '제출',
    cancel: '취소',
    confirm: '확인',
    back: '뒤로',
    close: '닫기',
    edit: '수정',
    delete: '삭제',
    save: '저장',
    next: '다음',
    previous: '이전',
    retry: '다시 시도',
    goBack: '돌아가기',
    goHome: '홈으로 돌아가기',
    continue: '계속하기',
    start: '시작하기',

    // Status
    loading: '불러오는 중...',
    submitting: '제출 중...',
    saving: '저장 중...',
    processing: '처리 중...',
    completed: '완료',
    failed: '실패',
    pending: '대기 중',
    inProgress: '진행 중',

    // Common messages
    success: '성공했습니다',
    error: '오류가 발생했습니다',
    noData: '데이터가 없습니다',
    unauthorized: '인증이 필요합니다',
    sessionExpired: '세션이 만료되었습니다. 다시 시작해주세요.',

    // Time
    hoursLeft: (hours: number) => `${hours}시간 남음`,
    daysLeft: (days: number) => `${days}일 남음`,
    hoursUntilOpen: (hours: number) => `${hours}시간 후 시작`,
    daysUntilOpen: (days: number) => `${days}일 후 시작`,
  },
  en: {
    // Buttons
    submit: 'Submit',
    cancel: 'Cancel',
    confirm: 'Confirm',
    back: 'Back',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    next: 'Next',
    previous: 'Previous',
    retry: 'Retry',
    goBack: 'Go Back',
    goHome: 'Go Home',
    continue: 'Continue',
    start: 'Start',

    // Status
    loading: 'Loading...',
    submitting: 'Submitting...',
    saving: 'Saving...',
    processing: 'Processing...',
    completed: 'Completed',
    failed: 'Failed',
    pending: 'Pending',
    inProgress: 'In Progress',

    // Common messages
    success: 'Success',
    error: 'An error occurred',
    noData: 'No data available',
    unauthorized: 'Authentication required',
    sessionExpired: 'Session expired. Please start again.',

    // Time
    hoursLeft: (hours: number) => `${hours} hours left`,
    daysLeft: (days: number) => `${days} days left`,
    hoursUntilOpen: (hours: number) => `Opens in ${hours} hours`,
    daysUntilOpen: (days: number) => `Opens in ${days} days`,
  },
};

// =============================================================================
// PAGE: PATIENT HOME
// =============================================================================

export const PAGE_PATIENT_HOME = {
  ko: {
    title: '검사 선택',
    loadingMessage: '설문을 불러오는 중...',
    errorLoadingSurveys: '설문 목록을 불러오지 못했습니다',
    errorStartingSurvey: '설문을 시작하지 못했습니다',
    noSurveysTitle: '참여 가능한 설문이 없습니다',
    noSurveysMessage: '현재 할당된 설문이 없습니다.',
    noSurveysNote: '나중에 다시 확인하거나 연구진에게 문의해주세요.',
    sectionAvailable: '진행 가능한 검사',
    sectionCompleted: '완료한 검사',
    sectionScheduledExpired: '예정 / 만료',
    deadlineImminent: '마감 임박',
    daysUntilDeadline: (days: number) => `마감까지 ${days}일`,
    warningHoursLeft: (hours: number) => `⚠️ ${hours}시간 남음`,
    daysUntilOpenWithDate: (days: number, date: string) => `${days}일 후 시작 (${date})`,
    expired: (date: string) => `만료됨 (${date})`,
    completed: (date: string) => `완료 (${date})`,
    visit: (num: number) => `Visit ${num}`,
    dismiss: 'Dismiss',
    footerHelp: '도움이 필요하시면 연구진에게 문의하세요',
  },
  en: {
    title: 'Select Survey',
    loadingMessage: 'Loading surveys...',
    errorLoadingSurveys: 'Failed to load survey list',
    errorStartingSurvey: 'Failed to start survey',
    noSurveysTitle: 'No surveys available',
    noSurveysMessage: 'You have no assigned surveys at this time.',
    noSurveysNote: 'Please check back later or contact the research team.',
    sectionAvailable: 'Available Surveys',
    sectionCompleted: 'Completed Surveys',
    sectionScheduledExpired: 'Scheduled / Expired',
    deadlineImminent: 'Deadline Imminent',
    daysUntilDeadline: (days: number) => `${days} days until deadline`,
    warningHoursLeft: (hours: number) => `⚠️ ${hours} hours left`,
    daysUntilOpenWithDate: (days: number, date: string) => `Opens in ${days} days (${date})`,
    expired: (date: string) => `Expired (${date})`,
    completed: (date: string) => `Completed (${date})`,
    visit: (num: number) => `Visit ${num}`,
    dismiss: 'Dismiss',
    footerHelp: 'Contact the research team if you need assistance',
  },
};

// =============================================================================
// PAGE: SETTINGS
// =============================================================================

export const PAGE_SETTINGS = {
  ko: {
    title: '설정',
    fontSizeSection: '글자 크기',
    languageSection: '언어',
    versionSection: '버전 정보',
    version: (ver: string) => `버전 ${ver}`,
  },
  en: {
    title: 'Settings',
    fontSizeSection: 'Font Size',
    languageSection: 'Language',
    versionSection: 'Version Info',
    version: (ver: string) => `Version ${ver}`,
  },
};

// =============================================================================
// PAGE: PROFILE
// =============================================================================

export const PAGE_PROFILE_INFO = {
  ko: {
    title: '프로필 정보',
    systemId: (id: string) => `시스템 ID: ${id}`,
    externalId: (id: string) => `ID: ${id}`,
    menuProfileEdit: '프로필 수정',
    menuSurveyHistory: '설문 이력',
    menuConsent: '개인정보 동의 확인',
    menuTraining: '설문 연습 확인',
    menuSettings: '설정',
    version: (ver: string) => `버전 ${ver}`,
    logoutButton: '로그아웃',
    errorNoProfile: '프로필 정보를 불러올 수 없습니다',
    loadingAuth: '인증 확인 중...',
    patientFallbackName: '환자',
  },
  en: {
    title: 'Profile Info',
    systemId: (id: string) => `System ID: ${id}`,
    externalId: (id: string) => `ID: ${id}`,
    menuProfileEdit: 'Edit Profile',
    menuSurveyHistory: 'Survey History',
    menuConsent: 'Review Consent',
    menuTraining: 'Review Training',
    menuSettings: 'Settings',
    version: (ver: string) => `Version ${ver}`,
    logoutButton: 'Logout',
    errorNoProfile: 'Failed to load profile information',
    loadingAuth: 'Verifying authentication...',
    patientFallbackName: 'Patient',
  },
};

export const PAGE_PROFILE_EDIT = {
  ko: {
    title: '프로필 수정',
    notice: 'ⓘ 변경 사항은 관리자 승인 후 반영됩니다.',
    nameLabel: '이름',
    namePlaceholder: '이름을 입력하세요',
    phoneLabel: '전화번호',
    phonePlaceholder: '010-4003-8107',
    genderLabel: '남성 / 여성 (선택사항)',
    genderMale: '남성',
    genderFemale: '여성',
    birthDateLabel: '생년월일',
    birthDatePlaceholder: '1990.03.12',
    submitButton: '변경 요청',
    successMessage: '변경 요청이 제출되었습니다.\\n관리자 승인 후 반영됩니다.',
    errorNoProfile: '프로필 정보를 불러올 수 없습니다',
    loadingAuth: '인증 확인 중...',
  },
  en: {
    title: 'Edit Profile',
    notice: 'ⓘ Changes will be applied after admin approval.',
    nameLabel: 'Name',
    namePlaceholder: 'Enter your name',
    phoneLabel: 'Phone Number',
    phonePlaceholder: '010-4003-8107',
    genderLabel: 'Gender (Optional)',
    genderMale: 'Male',
    genderFemale: 'Female',
    birthDateLabel: 'Birth Date',
    birthDatePlaceholder: '1990.03.12',
    submitButton: 'Submit Request',
    successMessage: 'Change request submitted.\\nChanges will be applied after admin approval.',
    errorNoProfile: 'Failed to load profile information',
    loadingAuth: 'Verifying authentication...',
  },
};

// =============================================================================
// PAGE: HISTORY
// =============================================================================

export const PAGE_HISTORY = {
  ko: {
    title: '설문 이력',
    loading: '이력을 불러오는 중...',
    loadingAuth: '인증 확인 중...',
  },
  en: {
    title: 'Survey History',
    loading: 'Loading history...',
    loadingAuth: 'Verifying authentication...',
  },
};

// =============================================================================
// PAGE: COMPLETE
// =============================================================================

export const PAGE_COMPLETE = {
  ko: {
    title: '감사합니다!',
    messageSuccess: '설문이 성공적으로 제출되었습니다.',
    consentTitle: '동의가 완료되었습니다.',
    consentMessageSuccess: '개인정보 동의가 성공적으로 제출되었습니다.',
    messageQueued: '설문이 전송 대기열에 추가되었습니다.',
    submessage: '설문에 응답해 주셔서 감사합니다. 귀하의 응답은 더 나은 의료 서비스를 제공하는 데 도움이 될 것입니다.',
    submittedLabel: '제출 시간:',
    referenceLabel: '참조 번호:',
    consentDocumentLabel: '동의 문서:',
    offlineNotice: '📡 인터넷 연결이 복구되면 자동으로 서버에 전송됩니다.',
    syncStatusNotice: '동기화 상태는 우측 하단에서 확인할 수 있습니다.',
    goHomeButton: '홈으로 돌아가기',
    contactNotice: '제출에 대해 궁금한 점이 있으시면 담당 의료진에게 문의하세요.',
  },
  en: {
    title: 'Thank You!',
    messageSuccess: 'Your survey has been submitted successfully.',
    consentTitle: 'Consent Completed',
    consentMessageSuccess: 'Your privacy consent has been submitted successfully.',
    messageQueued: 'Your survey has been queued for submission.',
    submessage: 'We appreciate you taking the time to complete this survey. Your responses will help us provide better care.',
    submittedLabel: 'Submitted:',
    referenceLabel: 'Reference:',
    consentDocumentLabel: 'Consent Document:',
    offlineNotice: '📡 Your data will be automatically sent to the server when internet connection is restored.',
    syncStatusNotice: 'Check sync status at the bottom right corner.',
    goHomeButton: 'Go Home',
    contactNotice: 'If you have any questions about your submission, please contact your healthcare provider.',
  },
};

// =============================================================================
// COMPONENT: REVIEW SCREEN (기존)
// =============================================================================

export const REVIEW_SCREEN_TEXT = {
  ko: {
    title: '검토 및 제출',
    description: '아래에서 답변을 검토하세요. 연필 아이콘을 클릭하여 답변을 수정할 수 있습니다. 제출 후에는 답변을 변경할 수 없습니다.',
    questionNumber: (num: number) => `질문 ${num}`,
    editButton: '수정',
    backButton: '질문으로 돌아가기',
    submitButton: '답변 제출',
    resubmitButton: '재제출',
    submittingButton: '제출 중...',
    noAnswer: '(답변 없음)',
    confirmTitle: '설문 데이터를 제출하시겠습니까?',
    confirmMessage: '제출하면 답변을 변경할 수 없습니다. 계속하기 전에 모든 응답이 정확한지 확인하세요.',
    confirmSubmit: '제출하기',
    confirmCancel: '취소',
    backLabel: '뒤로 가기',
  },
  en: {
    title: 'Review and Submit',
    description: 'Please review your answers below. You can edit any answer by clicking the pencil icon. Once submitted, your answers cannot be changed.',
    questionNumber: (num: number) => `Question ${num}`,
    editButton: 'Edit',
    backButton: 'Back to Questions',
    submitButton: 'Submit Your Data',
    resubmitButton: 'Resubmit',
    submittingButton: 'Submitting...',
    noAnswer: '(No answer)',
    confirmTitle: 'Submit Your Survey Data?',
    confirmMessage: 'Once you submit, you will not be able to change your answers. Please make sure all your responses are correct before proceeding.',
    confirmSubmit: 'Submit',
    confirmCancel: 'Cancel',
    backLabel: 'Go back',
  },
};

// =============================================================================
// COMPONENT: BOTTOM NAVIGATION
// =============================================================================

export const COMPONENT_BOTTOM_NAV = {
  ko: {
    home: '홈',
    history: '이력',
    profile: '프로필',
    ariaLabel: '메인 네비게이션',
  },
  en: {
    home: 'Home',
    history: 'History',
    profile: 'Profile',
    ariaLabel: 'Main navigation',
  },
};

// =============================================================================
// COMPONENT: SURVEY CARD
// =============================================================================

export const COMPONENT_SURVEY_PROGRESS = {
  ko: {
    questionOf: (current: number, total: number) => `${total}문항 중 ${current}번째`,
    showingQuestion: (current: number, total: number) => `${total}문항 중 ${current}번째 보는 중`,
    progressAriaLabel: (percentage: number) => `설문 진행률: ${percentage}% 완료`,
    questionTitle: (n: number) => `${n}번 문항`,
    previous: '이전',
    next: '다음',
    reviewAnswers: '답변 검토',
    validation: {
      required: '필수 질문입니다',
      minLength: (min: number) => `최소 ${min}자 이상 입력해주세요`,
      maxLength: (max: number) => `최대 ${max}자까지 입력 가능합니다`,
      invalidFormat: '올바른 형식이 아닙니다',
      scaleIntegerOnly: '정수를 입력해주세요',
      scaleRange: '0~10 사이의 값을 입력해주세요',
      scaleMin: (min: number) => `${min} 이상의 값을 입력해주세요`,
      scaleMax: (max: number) => `${max} 이하의 값을 입력해주세요`,
      numberMin: (min: number) => `${min} 이상의 값을 입력해주세요`,
      numberMax: (max: number) => `${max} 이하의 값을 입력해주세요`,
      numberInvalid: '유효한 숫자를 입력해주세요',
      integerOnly: '정수를 입력해주세요',
    },
    placeholder: {
      text: '답변을 입력해주세요...',
      textarea: '답변을 입력해주세요...',
      number: '숫자를 입력해주세요...',
      integerNumber: '정수를 입력해주세요...',
      selectRequired: '옵션을 선택해주세요...',
      selectOptional: '옵션 선택 (선택사항)',
    },
    dateInput: {
      placeholder: 'YYYY. MM. DD.',
      openCalendar: '날짜 선택',
      clear: '지우기',
      cancel: '취소',
      confirm: '확인',
      headers: {
        year: '년',
        month: '월',
        day: '일',
      },
    },
    queryBanner: '수정 요청된 문항',
    queryTargetOnly: '쿼리 대상 문항만 보기',
    queryTargetAll: '전체 문항 보기',
    queryTargetEmpty: '표시할 쿼리 대상 문항이 없습니다.',
    previousAnswer: '이전 답변',
    noQuestions: '이 설문에 질문이 없습니다.',
    backLabel: '뒤로 가기',
    previousQuestionLabel: '이전 질문',
    loadingQuestion: '질문을 불러오는 중...',
    scaleLow: '낮음',
    scaleHigh: '높음',
  },
  en: {
    questionOf: (current: number, total: number) => `Question ${current} of ${total}`,
    showingQuestion: (current: number, total: number) => `Showing question ${current} of ${total}`,
    progressAriaLabel: (percentage: number) => `Survey progress: ${percentage}% complete`,
    questionTitle: (n: number) => `Question ${n}`,
    previous: 'Previous',
    next: 'Next',
    reviewAnswers: 'Review Answers',
    validation: {
      required: 'This question is required',
      minLength: (min: number) => `Answer must be at least ${min} characters`,
      maxLength: (max: number) => `Answer must be no more than ${max} characters`,
      invalidFormat: 'Answer format is invalid',
      scaleIntegerOnly: 'Scale value must be a whole number',
      scaleRange: 'Scale value must be between 0 and 10',
      scaleMin: (min: number) => `Value must be at least ${min}`,
      scaleMax: (max: number) => `Value must be no more than ${max}`,
      numberMin: (min: number) => `Value must be at least ${min}`,
      numberMax: (max: number) => `Value must be no more than ${max}`,
      numberInvalid: 'Please enter a valid number',
      integerOnly: 'Please enter a whole number',
    },
    placeholder: {
      text: 'Enter your answer...',
      textarea: 'Enter your response...',
      number: 'Enter a number...',
      integerNumber: 'Enter a whole number...',
      selectRequired: 'Select an option...',
      selectOptional: 'Select an option (optional)',
    },
    dateInput: {
      placeholder: 'MM/DD/YYYY',
      openCalendar: 'Select date',
      clear: 'Clear',
      cancel: 'Cancel',
      confirm: 'Apply',
      headers: {
        year: 'Year',
        month: 'Month',
        day: 'Day',
      },
    },
    queryBanner: 'Correction Requested',
    queryTargetOnly: 'Only Query Target Questions',
    queryTargetAll: 'Show All Questions',
    queryTargetEmpty: 'There are no query target questions to display.',
    previousAnswer: 'Previous answer',
    noQuestions: 'No questions available for this survey.',
    backLabel: 'Go back',
    previousQuestionLabel: 'Previous question',
    loadingQuestion: 'Loading question...',
    scaleLow: 'Low',
    scaleHigh: 'High',
  },
};

export const COMPONENT_SURVEY_CARD = {
  ko: {
    defaultSurveyTitle: '설문',
    week: (num: number) => `Week ${num}`,
    estimatedTime: (minutes: number) => `약 ${minutes}분 소요`,
    dDay: (days: number) => `D-${days}`,
    today: 'D-day',
  },
  en: {
    defaultSurveyTitle: 'Survey',
    week: (num: number) => `Week ${num}`,
    estimatedTime: (minutes: number) => `~${minutes} min`,
    dDay: (days: number) => `D-${days}`,
    today: 'D-day',
  },
};

// =============================================================================
// COMPONENT: SURVEY LIST
// =============================================================================

export const COMPONENT_SURVEY_LIST = {
  ko: {
    instructionTitle: '안내',
    instruction1: '아래 검사 중 진행할 항목을 선택하세요.',
    instruction2: '',
    startButton: (count: number) => `선택한 검사 시작 (${count}개)`,
    selectPrompt: '검사를 선택해주세요',
  },
  en: {
    instructionTitle: 'Instructions',
    instruction1: 'Please select the surveys you want to complete.',
    instruction2: '',
    startButton: (count: number) => `Start Selected Surveys (${count})`,
    selectPrompt: 'Please select surveys',
  },
};

export const COMPONENT_SEQUENCE_CONFIRM_MODAL = {
  ko: {
    title: '설문 실행 순서 확인',
    subtitle: '선택하신 검사를 다음 순서대로 진행합니다.',
    completedWarning: '이미 완료된 검사가 포함되어 있습니다. 다시 제출하시면 기존 응답이 수정됩니다.',
    completedBadge: '완료됨',
    cancelButton: '취소',
    startButton: '시작',
  },
  en: {
    title: 'Confirm Survey Sequence',
    subtitle: 'The selected surveys will be conducted in the following order.',
    completedWarning: 'Some completed surveys are included. Re-submitting will modify previous responses.',
    completedBadge: 'Completed',
    cancelButton: 'Cancel',
    startButton: 'Start',
  },
};

// =============================================================================
// PAGE: TOKEN SESSION LIST
// =============================================================================

export const PAGE_TOKEN_SESSION_LIST = {
  ko: {
    title: '검사 선택',
    subtitle: '오늘 진행할 검사를 선택해주세요.',
    noSessionInfo: '세션 정보 없음',
    noSurveys: '설문이 없습니다.',
    errorStartingSurvey: '설문을 시작하지 못했습니다',
    confirmTitle: '⚠️ 이미 완료된 검사',
    confirmMessage: '이미 완료된 검사입니다. 다시 진행하시겠습니까?',
    confirmSubMessage: '사유 입력이 요구됩니다',
    todaySection: '오늘 해야 할 설문',
    todayAllDone: '오늘 설문을 완료하였습니다',
    futureSection: '예정된 설문',
    completedSection: '완료된 설문',
    noTodaySurveys: '오늘 해야 할 설문이 없습니다',
    noFutureSurveys: '예정된 설문이 없습니다',
    noCompletedSurveys: '완료된 설문이 없습니다',
    errorLoadingSessions: '세션 정보를 불러오지 못했습니다',
    loading: '로딩 중...',
    queryBanner: '수정 요청이 있습니다! 아래 완료된 설문을 확인해 주세요.',
    unknownSurvey: '알 수 없는 설문',
  },
  en: {
    title: 'Select Survey',
    subtitle: 'Please select the survey you want to complete today.',
    noSessionInfo: 'No session information',
    noSurveys: 'No surveys available.',
    errorStartingSurvey: 'Failed to start survey',
    confirmTitle: '⚠️ Already Completed Survey',
    confirmMessage: 'This survey has already been completed. Do you want to proceed again?',
    confirmSubMessage: 'Reason for modification is required',
    todaySection: 'Today\'s Surveys',
    todayAllDone: 'You have completed all surveys for today',
    futureSection: 'Upcoming Surveys',
    completedSection: 'Completed Surveys',
    noTodaySurveys: 'No surveys scheduled for today',
    noFutureSurveys: 'No upcoming surveys',
    noCompletedSurveys: 'No completed surveys',
    errorLoadingSessions: 'Failed to load session information',
    loading: 'Loading...',
    queryBanner: 'You have correction requests! Please check the completed surveys below.',
    unknownSurvey: 'Unknown survey',
  },
};

// =============================================================================
// PAGE: SESSION SURVEY
// =============================================================================

export const PAGE_SESSION_SURVEY = {
  ko: {
    loading: '설문을 불러오는 중...',
    errorTitle: '설문 로드 실패',
    errorNoSurveyInfo: '설문 정보가 없습니다.',
    errorNoSurveyId: 'Survey ID를 찾을 수 없습니다.',
    errorLoadingSurvey: '설문을 불러올 수 없습니다.',
    errorNoSessionId: 'Session ID가 없습니다.',
    errorNoVersionId: 'Survey Version ID가 없습니다.',
    errorNoModificationReason: '수정 사유가 필요합니다.',
    errorNotFound: '설문을 찾을 수 없습니다.',
    backToStart: '처음으로',
    sequenceProgress: (current: number, total: number) => `설문 ${current}/${total} 진행 중`,
  },
  en: {
    loading: 'Loading survey...',
    errorTitle: 'Failed to Load Survey',
    errorNoSurveyInfo: 'No survey information available.',
    errorNoSurveyId: 'Survey ID not found.',
    errorLoadingSurvey: 'Failed to load survey.',
    errorNoSessionId: 'Session ID is missing.',
    errorNoVersionId: 'Survey Version ID is missing.',
    errorNoModificationReason: 'Modification reason is required.',
    errorNotFound: 'Survey not found.',
    backToStart: 'Back to Start',
    sequenceProgress: (current: number, total: number) => `Survey ${current}/${total} in progress`,
  },
};

// =============================================================================
// PAGE: ACTIVATION CODE SURVEY
// =============================================================================

export const PAGE_ACTIVATION_CODE_SURVEY = {
  ko: {
    loading: '설문을 불러오는 중...',
    sessionExpired: '세션이 만료되었습니다. 다시 시작해주세요.',
    retry: '다시 시도',
    notFound: '설문을 찾을 수 없습니다',
    notFoundMessage: '요청하신 설문을 찾을 수 없습니다. URL을 확인하고 다시 시도해주세요.',
    participantLabel: '참여자',
  },
  en: {
    loading: 'Loading survey...',
    sessionExpired: 'Session expired. Please start again.',
    retry: 'Retry',
    notFound: 'Survey Not Found',
    notFoundMessage: 'The requested survey could not be found. Please check the URL and try again.',
    participantLabel: 'Participant',
  },
};

// =============================================================================
// PAGE: TOKEN AUTH
// =============================================================================

export const PAGE_TOKEN_AUTH = {
  ko: {
    noToken: '토큰이 없습니다.',
  },
  en: {
    noToken: 'No token provided.',
  },
};

// =============================================================================
// PAGE: TOKEN ENTRY
// =============================================================================

export const PAGE_TOKEN_ENTRY = {
  ko: {
    noToken: '토큰이 없습니다.',
    invalidSession: '유효하지 않은 설문 링크입니다.',
    loadError: '설문을 불러올 수 없습니다.',
    errorTitle: '설문 접근 오류',
    loading: '설문을 불러오는 중...',
  },
  en: {
    noToken: 'No token provided.',
    invalidSession: 'Invalid survey link.',
    loadError: 'Failed to load survey.',
    errorTitle: 'Survey Access Error',
    loading: 'Loading survey...',
  },
};

// =============================================================================
// PAGE: PROFILE (Legacy)
// =============================================================================

export const PAGE_PROFILE = {
  ko: {
    title: '프로필',
    loading: 'Loading...',
    footerHelp: '도움이 필요하시면 연구진에게 문의해주세요.',
  },
  en: {
    title: 'Profile',
    loading: 'Loading...',
    footerHelp: 'Contact the research team if you need assistance.',
  },
};

// =============================================================================
// TOKEN SURVEY WIZARD
// =============================================================================

export const ACTIVATION_CODE_STEP = {
  ko: {
    title: '인증코드 입력',
    description: (digits: number) => `문자로 받은 ${digits}자리 인증코드를 입력해주세요.`,
    label: '인증코드',
    placeholder: '예: ABC123',
    submitting: '확인 중...',
    next: '다음',
    helpText1: '💡 인증코드는 문자메시지로 발송되었습니다.',
    helpText2: '받지 못하셨다면 관리자에게 문의해주세요.',
    errors: {
      network: '네트워크 오류가 발생했습니다.',
      invalidCode: '인증코드가 올바르지 않습니다.',
      surveyNotFound: '설문을 찾을 수 없습니다.',
      expired: '설문 응답 기간이 만료되었습니다.',
      authFailed: '인증에 실패했습니다.',
    },
  },
  en: {
    title: 'Enter Activation Code',
    description: (digits: number) => `Please enter the ${digits}-digit activation code you received via SMS.`,
    label: 'Activation Code',
    placeholder: 'e.g., ABC123',
    submitting: 'Verifying...',
    next: 'Next',
    helpText1: '💡 The activation code was sent via SMS.',
    helpText2: 'If you didn\'t receive it, please contact the administrator.',
    errors: {
      network: 'A network error occurred.',
      invalidCode: 'The activation code is incorrect.',
      surveyNotFound: 'Survey not found.',
      expired: 'The survey response period has expired.',
      authFailed: 'Authentication failed.',
    },
  },
};

export const BIRTH_DATE_STEP = {
  ko: {
    title: '생년월일 확인',
    description: (name: string) => `${name}님의 생년월일을 입력해주세요.`,
    label: '생년월일',
    placeholder: 'YYYYMMDD',
    hint: '예: 1990년 1월 1일 → 1990 / 01 / 01',
    back: '이전',
    submitting: '확인 중...',
    next: '다음',
    helpText: '💡 출생 연도, 월, 일을 각 칸에 입력해주세요.',
    errors: {
      network: '네트워크 오류가 발생했습니다.',
      sessionExpired: '인증이 만료되었습니다. 처음부터 다시 시도해주세요.',
      authTimeout: '인증 시간이 만료되었습니다. 처음부터 다시 시도해주세요.',
      mismatch: '생년월일이 일치하지 않습니다.',
      surveyNotFound: '설문을 찾을 수 없습니다.',
      authFailed: '인증에 실패했습니다.',
    },
  },
  en: {
    title: 'Verify Birth Date',
    description: (name: string) => `Please enter the birth date for ${name}.`,
    label: 'Birth Date',
    placeholder: 'YYYYMMDD',
    hint: 'e.g., Jan 1, 1990 → 1990 / 01 / 01',
    back: 'Back',
    submitting: 'Verifying...',
    next: 'Next',
    helpText: '💡 Enter your birth year, month, and day in each field.',
    errors: {
      network: 'A network error occurred.',
      sessionExpired: 'Authentication expired. Please start over.',
      authTimeout: 'Authentication timeout. Please start over.',
      mismatch: 'Birth date does not match.',
      surveyNotFound: 'Survey not found.',
      authFailed: 'Authentication failed.',
    },
  },
};


// =============================================================================
// COMPONENT: AUTH
// =============================================================================

export const COMPONENT_AUTH = {
  ko: {
    // Common labels
    activationCodeLabel: '인증코드',
    otpLabel: '인증번호',
    birthDateLabel: '생년월일',

    // Placeholders
    activationCodePlaceholder: '예: A3X7K9',
    birthDatePlaceholder: '예: 19900115 또는 900115',

    // Hints
    activationCodeHint: 'SMS에 포함된 6자리 인증코드',
    birthDateHint: '8자리(YYYYMMDD) 또는 6자리(YYMMDD)',

    // Buttons
    next: '다음',
    verify: '인증하기',
    confirm: '확인',
    back: '이전',
    cancel: '취소',
    requestOtp: '인증번호 요청',
    resendOtp: '인증번호 재전송',
    regenerateCode: '인증코드 재발급',

    // Titles
    activationCodeTitle: '인증코드 입력',
    otpInputTitle: '인증번호 입력',
    otpRequestTitle: '본인 인증',
    birthDateTitle: '생년월일 확인',

    // Descriptions
    surveyDescription: (title: string) => `<strong>${title}</strong> 설문에 참여하기 위해`,
    activationCodeDescription: 'SMS로 받으신 인증코드를 입력해주세요.',
    combinedDescription: 'SMS로 받으신 인증코드와 생년월일을 입력해주세요.',
    otpRequestDescription1: '설문에 참여하기 위해 본인 인증이 필요합니다.',
    otpRequestDescription2: '등록된 휴대폰 번호로 인증번호를 발송합니다.',
    otpInputDescription: (phone: string) => `<strong>${phone}</strong>로 전송된`,
    otpInputDescription2: '인증번호 6자리를 입력해주세요.',
    birthDateDescription1: '본인 확인을 위해',
    birthDateDescription2: '생년월일을 입력해주세요.',

    // Labels with participant
    participantLabel: (name: string) => `참여자: ${name}`,

    // Time
    expiresIn: (minutes: number) => `유효시간: ${minutes}분`,
    remainingAttempts: (count: number) => `남은 시도 횟수: ${count}회`,

    // ARIA labels
    otpInputGroup: 'OTP 입력',
    otpInputDigit: (index: number) => `OTP ${index + 1}번째 숫자`,
    logoAlt: '기관 로고',

    // Validation errors
    validationErrors: {
      activationCodeRequired: '인증코드 6자리를 입력해주세요.',
      otpRequired: '인증번호 6자리를 입력해주세요.',
      birthDateRequired: '생년월일을 입력해주세요.',
    },

    // API errors
    errors: {
      network: '네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.',
      invalidCode: '인증코드가 일치하지 않습니다. SMS 메시지를 확인해주세요.',
      invalidOtp: (attempts?: number) =>
        `인증번호가 일치하지 않습니다.${attempts !== undefined ? ` (남은 시도: ${attempts}회)` : ''}`,
      otpExpired: '인증번호가 만료되었습니다. 다시 요청해주세요.',
      birthDateMismatch: (attempts?: number) =>
        `생년월일이 일치하지 않습니다.${attempts !== undefined ? ` (남은 시도: ${attempts}회)` : ''}`,
      maxAttemptsExceeded: '최대 시도 횟수를 초과했습니다. 인증번호를 다시 요청해주세요.',
      sessionNotFound: '설문 세션을 찾을 수 없습니다.',
      sessionExpired: '설문 응답 기간이 만료되었습니다.',
      alreadySubmitted: '이미 제출된 설문입니다.',
      authFailed: '인증에 실패했습니다. 다시 시도해주세요.',
      invalidSession: '유효하지 않은 설문 링크입니다.',
      invalidAuthSession: '인증 세션이 유효하지 않습니다. 처음부터 다시 시도해주세요.',
      authTimeout: '인증 시간이 만료되었습니다. 처음부터 다시 시도해주세요.',
      participantNotFound: '참여자 정보를 찾을 수 없습니다.',
      noPhoneNumber: '등록된 전화번호가 없습니다. 연구진에게 문의해주세요.',
      otpSendFailed: '인증번호 발송에 실패했습니다. 다시 시도해주세요.',
      tryAgainLater: '잠시 후 다시 시도해주세요.',
      noSessionInfo: '세션 정보가 없습니다.',
      codeRegenerateSuccess: '새로운 인증코드가 SMS로 발송되었습니다. 메시지를 확인해주세요.',
      codeRegenerateFailed: '인증코드 재발급에 실패했습니다. 연구진에게 문의해주세요.',
    },
  },
  en: {
    // Common labels
    activationCodeLabel: 'Activation Code',
    otpLabel: 'Verification Code',
    birthDateLabel: 'Birth Date',

    // Placeholders
    activationCodePlaceholder: 'e.g., A3X7K9',
    birthDatePlaceholder: 'e.g., 19900115 or 900115',

    // Hints
    activationCodeHint: '6-digit code from SMS',
    birthDateHint: '8 digits (YYYYMMDD) or 6 digits (YYMMDD)',

    // Buttons
    next: 'Next',
    verify: 'Verify',
    confirm: 'Confirm',
    back: 'Back',
    cancel: 'Cancel',
    requestOtp: 'Request Code',
    resendOtp: 'Resend Code',
    regenerateCode: 'Regenerate Code',

    // Titles
    activationCodeTitle: 'Enter Activation Code',
    otpInputTitle: 'Enter Verification Code',
    otpRequestTitle: 'Identity Verification',
    birthDateTitle: 'Verify Birth Date',

    // Descriptions
    surveyDescription: (title: string) => `To participate in the <strong>${title}</strong> survey`,
    activationCodeDescription: 'Please enter the activation code you received via SMS.',
    combinedDescription: 'Please enter the activation code and birth date you received via SMS.',
    otpRequestDescription1: 'Identity verification is required to participate in the survey.',
    otpRequestDescription2: 'A verification code will be sent to your registered phone number.',
    otpInputDescription: (phone: string) => `Sent to <strong>${phone}</strong>`,
    otpInputDescription2: 'Please enter the 6-digit verification code.',
    birthDateDescription1: 'For identity verification,',
    birthDateDescription2: 'please enter your birth date.',

    // Labels with participant
    participantLabel: (name: string) => `Participant: ${name}`,

    // Time
    expiresIn: (minutes: number) => `Valid for: ${minutes} min`,
    remainingAttempts: (count: number) => `Remaining attempts: ${count}`,

    // ARIA labels
    otpInputGroup: 'OTP input',
    otpInputDigit: (index: number) => `OTP digit ${index + 1}`,
    logoAlt: 'Organization logo',

    // Validation errors
    validationErrors: {
      activationCodeRequired: 'Please enter the 6-digit activation code.',
      otpRequired: 'Please enter the 6-digit verification code.',
      birthDateRequired: 'Please enter your birth date.',
    },

    // API errors
    errors: {
      network: 'A network error occurred. Please check your internet connection.',
      invalidCode: 'The activation code is incorrect. Please check your SMS.',
      invalidOtp: (attempts?: number) =>
        `The verification code is incorrect.${attempts !== undefined ? ` (${attempts} attempts remaining)` : ''}`,
      otpExpired: 'The verification code has expired. Please request a new one.',
      birthDateMismatch: (attempts?: number) =>
        `Birth date does not match.${attempts !== undefined ? ` (${attempts} attempts remaining)` : ''}`,
      maxAttemptsExceeded: 'Maximum attempts exceeded. Please request a new verification code.',
      sessionNotFound: 'Survey session not found.',
      sessionExpired: 'The survey response period has expired.',
      alreadySubmitted: 'This survey has already been submitted.',
      authFailed: 'Authentication failed. Please try again.',
      invalidSession: 'Invalid survey link.',
      invalidAuthSession: 'Authentication session is invalid. Please start over.',
      authTimeout: 'Authentication timeout. Please start over.',
      participantNotFound: 'Participant information not found.',
      noPhoneNumber: 'No registered phone number. Please contact the research team.',
      otpSendFailed: 'Failed to send verification code. Please try again.',
      tryAgainLater: 'Please try again later.',
      noSessionInfo: 'No session information available.',
      codeRegenerateSuccess: 'A new activation code has been sent via SMS. Please check your messages.',
      codeRegenerateFailed: 'Failed to regenerate activation code. Please contact the research team.',
    },
  },
};

// =============================================================================
// COMPONENT: SURVEY
// =============================================================================

export const COMPONENT_RESUBMIT_MODAL = {
  ko: {
    title: '수정 사유 입력',
    description: '설문 응답을 수정하는 이유를 입력해주세요.',
    minLengthNote: (min: number) => `(최소 ${min}자 이상)`,
    placeholder: '수정 사유를 직접 입력해주세요.',
    ariaLabel: '수정 사유',
    charCount: (current: number, min: number) => `${current} / ${min}자`,
    cancel: '취소',
    confirm: '확인',
    keyboardHint: (esc: string, ctrlEnter: string) => `${esc} 취소 • ${ctrlEnter} 확인`,
    validationError: (min: number) => `수정 사유는 최소 ${min}자 이상 입력해야 합니다.`,
    selectPlaceholder: '수정 사유를 선택해주세요',
    manualOption: '직접 입력',
    presets: [] as string[], // 프로젝트 설정 또는 DEFAULT_REASON_PRESETS에서 동적 로드
  },
  en: {
    title: 'Enter Modification Reason',
    description: 'Please enter the reason for modifying your survey response.',
    minLengthNote: (min: number) => `(Minimum ${min} characters)`,
    placeholder: 'Please enter your modification reason.',
    ariaLabel: 'Modification reason',
    charCount: (current: number, min: number) => `${current} / ${min} chars`,
    cancel: 'Cancel',
    confirm: 'Confirm',
    keyboardHint: (esc: string, ctrlEnter: string) => `${esc} Cancel • ${ctrlEnter} Confirm`,
    validationError: (min: number) => `Modification reason must be at least ${min} characters.`,
    selectPlaceholder: 'Select a reason',
    manualOption: 'Enter manually',
    presets: [] as string[], // 프로젝트 설정 또는 DEFAULT_REASON_PRESETS에서 동적 로드
  },
};

/** 시스템 기본 수정 사유 프리셋 (다국어) */
export const DEFAULT_REASON_PRESETS: Array<{ ko: string; en: string; id: string }> = [
  { ko: '이전 답변에 오류가 있어서 수정합니다.', en: 'Correcting an error in my previous response.', id: 'Memperbaiki kesalahan pada jawaban sebelumnya.' },
  { ko: '실수로 잘못 선택하여 수정합니다.', en: 'I accidentally selected the wrong answer.', id: 'Saya tidak sengaja memilih jawaban yang salah.' },
  { ko: '다시 확인한 결과 답변을 변경합니다.', en: 'Updating my answer after reconsideration.', id: 'Memperbarui jawaban setelah pertimbangan ulang.' },
  { ko: '담당자 안내에 따라 수정합니다.', en: 'Modifying as advised by my coordinator.', id: 'Mengubah sesuai arahan koordinator.' },
];

export const COMPONENT_LOCKED_SURVEY = {
  ko: {
    notYetStarted: {
      primary: '🔒 아직 시작 시간이 아닙니다',
      secondary: (dateTime: string) => `${dateTime} 부터 작성 가능합니다`,
    },
    expired: {
      primary: '🔒 제출 기간이 만료되었습니다',
      secondary: (dateTime: string) => `마감: ${dateTime}`,
    },
    noVisitTime: {
      primary: '🔒 방문 시간 정보가 필요합니다',
      secondary: '담당자에게 문의해 주세요',
    },
    default: {
      primary: '🔒 설문을 사용할 수 없습니다',
    },
    timeRemaining: {
      days: (d: number, h: number) => `${d}일 ${h}시간`,
      hours: (h: number) => `${h}시간`,
      minutes: (m: number) => `${m}분`,
      suffix: '남음',
    },
  },
  en: {
    notYetStarted: {
      primary: '🔒 Not yet available',
      secondary: (dateTime: string) => `Available from ${dateTime}`,
    },
    expired: {
      primary: '🔒 Submission period has expired',
      secondary: (dateTime: string) => `Deadline: ${dateTime}`,
    },
    noVisitTime: {
      primary: '🔒 Visit time information required',
      secondary: 'Please contact your coordinator',
    },
    default: {
      primary: '🔒 Survey is not available',
    },
    timeRemaining: {
      days: (d: number, h: number) => `${d}d ${h}h`,
      hours: (h: number) => `${h} hours`,
      minutes: (m: number) => `${m} min`,
      suffix: 'remaining',
    },
  },
};

// =============================================================================
// COMPONENT: MISC
// =============================================================================

export const COMPONENT_USER_INFO_CARD = {
  ko: {
    defaultName: '환자',
  },
  en: {
    defaultName: 'Patient',
  },
};

export const COMPONENT_CONFIRM_MODAL = {
  ko: {
    no: '아니오',
    yes: '예',
  },
  en: {
    no: 'No',
    yes: 'Yes',
  },
};

export const COMPONENT_PROFILE = {
  ko: {
    header: {
      participantLabel: '참여자',
    },
    actions: {
      settings: '설정',
      logout: '로그아웃',
    },
    info: {
      sectionTitle: '참여 정보',
      participantId: '참여자 ID',
      status: '상태',
      statusActive: '활성',
      project: '프로젝트',
    },
  },
  en: {
    header: {
      participantLabel: 'Participant',
    },
    actions: {
      settings: 'Settings',
      logout: 'Logout',
    },
    info: {
      sectionTitle: 'Participant Information',
      participantId: 'Participant ID',
      status: 'Status',
      statusActive: 'Active',
      project: 'Project',
    },
  },
};

export const COMPONENT_EMPTY_HISTORY = {
  ko: {
    title: '완료한 설문이 없습니다',
    description: '설문을 완료하면 이곳에서 이력을 확인할 수 있습니다.',
  },
  en: {
    title: 'No completed surveys',
    description: 'When you complete surveys, you can view your history here.',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export const getCommonText = (lang: Language) => COMMON_TEXT[lang];
export const getPatientHomeText = (lang: Language) => PAGE_PATIENT_HOME[lang];
export const getSettingsText = (lang: Language) => PAGE_SETTINGS[lang];
export const getProfileInfoText = (lang: Language) => PAGE_PROFILE_INFO[lang];
export const getProfileEditText = (lang: Language) => PAGE_PROFILE_EDIT[lang];
export const getProfileText = (lang: Language) => PAGE_PROFILE[lang];
export const getHistoryText = (lang: Language) => PAGE_HISTORY[lang];
export const getCompleteText = (lang: Language) => PAGE_COMPLETE[lang];
export const getReviewText = (lang: Language) => REVIEW_SCREEN_TEXT[lang];
export const getBottomNavText = (lang: Language) => COMPONENT_BOTTOM_NAV[lang];
export const getSurveyCardText = (lang: Language) => COMPONENT_SURVEY_CARD[lang];
export const getSurveyListText = (lang: Language) => COMPONENT_SURVEY_LIST[lang];
export const getTokenSessionListText = (lang: Language) => PAGE_TOKEN_SESSION_LIST[lang];
export const getSessionSurveyText = (lang: Language) => PAGE_SESSION_SURVEY[lang];
export const getActivationCodeSurveyText = (lang: Language) => PAGE_ACTIVATION_CODE_SURVEY[lang];
export const getTokenAuthText = (lang: Language) => PAGE_TOKEN_AUTH[lang];
export const getTokenEntryText = (lang: Language) => PAGE_TOKEN_ENTRY[lang];
export const getActivationCodeStepText = (lang: Language) => ACTIVATION_CODE_STEP[lang];
export const getBirthDateStepText = (lang: Language) => BIRTH_DATE_STEP[lang];
export const getAuthText = (lang: Language) => COMPONENT_AUTH[lang];
export const getResubmitModalText = (lang: Language) => COMPONENT_RESUBMIT_MODAL[lang];
export const getLockedSurveyText = (lang: Language) => COMPONENT_LOCKED_SURVEY[lang];
export const getUserInfoCardText = (lang: Language) => COMPONENT_USER_INFO_CARD[lang];
export const getConfirmModalText = (lang: Language) => COMPONENT_CONFIRM_MODAL[lang];
export const getComponentProfileText = (lang: Language) => COMPONENT_PROFILE[lang];
export const getEmptyHistoryText = (lang: Language) => COMPONENT_EMPTY_HISTORY[lang];

// =============================================================================
// COMPONENT: PREREQUISITE SECTION (동의 + 연습 카드)
// =============================================================================

export const COMPONENT_PREREQUISITE = {
  ko: {
    sectionTitlePending: '먼저 완료해주세요',
    sectionTitleInfo: '연구 참여 정보',
    consentTitle: '개인정보 동의',
    consentDescPending: '연구 참여를 위한 개인정보 처리 동의가 필요합니다',
    consentDescSigned: '동의를 철회하려면 여기를 누르세요',
    consentAriaGo: '개인정보 동의 진행하기',
    consentAriaDone: '동의서 확인하기 (동의 완료)',
    trainingTitle: '설문 연습',
    trainingDescPending: '실제 설문 참여 전 연습을 완료해주세요',
    trainingDescDone: '연습을 완료하셨습니다',
    trainingAriaGo: '설문 연습 진행하기',
    trainingAriaDone: '설문 연습 확인하기 (완료)',
    badgeComplete: '완료',
    badgePending: '필요',
    badgeRequired: '필수',
  },
  en: {
    sectionTitlePending: 'Please complete first',
    sectionTitleInfo: 'Study Participation Info',
    consentTitle: 'Privacy Consent',
    consentDescPending: 'You must agree to the privacy policy to participate in this study',
    consentDescSigned: 'Tap here to withdraw your consent',
    consentAriaGo: 'Proceed to privacy consent',
    consentAriaDone: 'View consent (completed)',
    trainingTitle: 'Survey Training',
    trainingDescPending: 'Please complete the training before starting the actual survey',
    trainingDescDone: 'Training completed',
    trainingAriaGo: 'Proceed to survey training',
    trainingAriaDone: 'View survey training (completed)',
    badgeComplete: 'Done',
    badgePending: 'Needed',
    badgeRequired: 'Required',
  },
};

// =============================================================================
// COMPONENT: TRAINING CARD
// =============================================================================

export const COMPONENT_TRAINING_CARD = {
  ko: {
    actualSurvey: '실제 설문으로 연습',
    trainingVersion: '연습 전용 버전',
    starting: '이동 중...',
    start: '시작',
  },
  en: {
    actualSurvey: 'Practice with actual survey',
    trainingVersion: 'Training version',
    starting: 'Loading...',
    start: 'Start',
  },
};

// =============================================================================
// COMPONENT: SURVEY CARD (추가 키)
// =============================================================================

export const COMPONENT_SURVEY_CARD_EXTRA = {
  ko: {
    delayed: '지연',
    hoursRemaining: (hours: number) => `${hours}시간 남음`,
  },
  en: {
    delayed: 'Delayed',
    hoursRemaining: (hours: number) => `${hours}h left`,
  },
};

// =============================================================================
// COMPONENT: SESSION STATUS LABELS
// =============================================================================

export const SESSION_STATUS_LABELS = {
  ko: {
    waiting: '대기중',
    inProgress: '진행중',
    completed: '완료',
    missed: '미응답',
  },
  en: {
    waiting: 'Waiting',
    inProgress: 'In Progress',
    completed: 'Completed',
    missed: 'Missed',
  },
};

// =============================================================================
// UTILS: SURVEY LIST HELPERS
// =============================================================================

export const UTILS_SURVEY_LIST = {
  ko: {
    birthDateNotSet: '생년월일 미설정',
    genderNotSet: '성별 미설정',
    male: '남성',
    female: '여성',
    defaultDescription: '설문에 대한 설명입니다.',
    eqDescription: '이동능력, 자기관리, 일상활동, 통증/불편, 불안/우울 포함한 현재 건강 상태를 평가합니다.',
    vasDescription: '0에서 100까지의 점수로 선택하여 현재 건강 상태를 평가합니다.',
    durationDefault: '약 5분',
    durationEq: '약 2-3분',
    durationVas: '약 1분',
  },
  en: {
    birthDateNotSet: 'Birth date not set',
    genderNotSet: 'Gender not set',
    male: 'Male',
    female: 'Female',
    defaultDescription: 'Survey description.',
    eqDescription: 'Evaluate your current health status including mobility, self-care, usual activities, pain/discomfort, and anxiety/depression.',
    vasDescription: 'Rate your current health status on a scale from 0 to 100.',
    durationDefault: '~5 min',
    durationEq: '~2-3 min',
    durationVas: '~1 min',
  },
};

// =============================================================================
// COMPONENT: QUERY SECTION
// =============================================================================

export const COMPONENT_QUERY_SECTION = {
  ko: {
    sectionTitle: '관리자 확인 요청',
    queryTypeDataCorrection: '수정 요청',
    queryTypeDataConfirmation: '확인 요청',
    queryTypeFormReview: '재확인 요청',
  },
  en: {
    sectionTitle: 'Admin Review Requests',
    queryTypeDataCorrection: 'Correction Request',
    queryTypeDataConfirmation: 'Confirmation Request',
    queryTypeFormReview: 'Review Request',
  },
};

// =============================================================================
// COMPONENT: HISTORY CARD
// =============================================================================

export const COMPONENT_HISTORY_CARD = {
  ko: {
    completed: (date: string) => `완료: ${date}`,
    revisionCount: (count: number) => `수정 ${count}회`,
    lastRevision: (date: string) => `최근 수정: ${date}`,
  },
  en: {
    completed: (date: string) => `Completed: ${date}`,
    revisionCount: (count: number) => `${count} revision${count > 1 ? 's' : ''}`,
    lastRevision: (date: string) => `Last modified: ${date}`,
  },
};

// =============================================================================
// PAGE: TRAINING SURVEY
// =============================================================================

export const PAGE_TRAINING_SURVEY = {
  ko: {
    loading: '훈련 설문을 불러오는 중...',
    errorTitle: '오류',
    errorNotFound: '설문을 불러올 수 없습니다.',
    goBack: '뒤로 가기',
    banner: '연습 모드 — 이 응답은 연구 데이터에 포함되지 않습니다',
  },
  en: {
    loading: 'Loading training survey...',
    errorTitle: 'Error',
    errorNotFound: 'Unable to load the survey.',
    goBack: 'Go Back',
    banner: 'Training Mode — This response will not be included in study data',
  },
};

// =============================================================================
// PAGE: CONSENT LIST
// =============================================================================

export const PAGE_CONSENT_LIST = {
  ko: {
    title: '개인정보 동의 현황',
    loading: '불러오는 중...',
    errorLoading: '데이터를 불러오지 못했습니다.',
    empty: '동의서가 있는 프로젝트가 없습니다.',
    readonly: '수정 불가',
    consentSigned: '동의 완료',
    consentUnsigned: '미동의',
    consentDeclined: '동의 거부',
  },
  en: {
    title: 'Privacy Consent Status',
    loading: 'Loading...',
    errorLoading: 'Failed to load data.',
    empty: 'No projects with consent forms.',
    readonly: 'Read only',
    consentSigned: 'Signed',
    consentUnsigned: 'Unsigned',
    consentDeclined: 'Declined',
  },
};

// =============================================================================
// PAGE: CONSENT PAGE ROUTE
// =============================================================================

export const PAGE_CONSENT = {
  ko: {
    pageTitle: '임상시험 참여 동의서',
    loading: '동의서를 불러오는 중...',
    retry: '다시 시도',
    backLabel: '뒤로 가기',
    consentContentLabel: '동의서 내용',
    scrollHint: '내용을 끝까지 스크롤하면 버튼이 활성화됩니다.',
    agreeButton: '동의합니다',
    disagreeButton: '동의하지 않습니다',
    processing: '처리 중...',
    signError: '동의 처리 중 오류가 발생했습니다.',
    genericError: '예기치 않은 오류가 발생했습니다. 다시 시도해 주세요.',
    signatureTitle: '전자 서명',
    signatureHint: '아래 영역에 직접 서명해 주세요.',
    signatureRequired: '전자 서명을 입력해 주세요.',
    acknowledgementLabel: '본인은 본 동의서의 내용을 충분히 읽고 이해하였으며, 자유로운 의사에 따라 자발적으로 서명함을 확인합니다.',
    acknowledgementRequired: '동의서 확인 체크를 완료해 주세요.',
    // Already consented state
    alreadyConsentedTitle: '동의 완료',
    alreadyConsentedDesc: '동의서에 서명이 완료되었습니다.\n연구 참여를 중단하려면 아래의 동의 철회 버튼을 눌러주세요.',
    goToSurveyList: '설문 목록으로',
    withdrawButton: '동의 철회하기',
    // Declined state
    declinedTitle: '동의를 거부하셨습니다',
    declinedDesc: '동의서를 거부하셨습니다. 임상시험 참여를 위해서는 동의서에 서명하셔야 합니다.\n참여를 원하시면 연구 담당자에게 문의하여 주십시오.',
    goBack: '이전으로 돌아가기',
    // Decline confirmation modal
    declineConfirmTitle: '동의 거부 확인',
    declineConfirmDesc: '동의를 거부하시면 이 임상시험에 참여하실 수 없습니다. 정말 거부하시겠습니까?',
    declineConfirmYes: '예, 거부합니다',
    declineConfirmCancel: '뒤로 가기',
    // Withdraw confirmation modal
    withdrawConfirmTitle: '동의 철회 확인',
    withdrawConfirmDesc: '동의를 철회하시면 더 이상 이 임상시험에 참여하실 수 없습니다. 정말 철회하시겠습니까?',
    withdrawConfirmYes: '예, 철회합니다',
    withdrawConfirmCancel: '취소',
  },
  en: {
    pageTitle: 'Informed Consent',
    loading: 'Loading consent form...',
    retry: 'Retry',
    backLabel: 'Go back',
    consentContentLabel: 'Consent document',
    scrollHint: 'Scroll to the bottom to enable the buttons.',
    agreeButton: 'I Agree',
    disagreeButton: 'I Do Not Agree',
    processing: 'Processing...',
    signError: 'An error occurred while processing consent.',
    genericError: 'An unexpected error occurred. Please try again.',
    signatureTitle: 'Electronic Signature',
    signatureHint: 'Please sign directly in the box below.',
    signatureRequired: 'Please provide your electronic signature.',
    acknowledgementLabel: 'I confirm that I have fully read and understood this consent form, and that I am signing voluntarily of my own free will.',
    acknowledgementRequired: 'Please complete the consent acknowledgement checkbox.',
    alreadyConsentedTitle: 'Consent Completed',
    alreadyConsentedDesc: 'You have already signed the consent form.\nTo withdraw from the study, tap the withdraw button below.',
    goToSurveyList: 'Go to Survey List',
    withdrawButton: 'Withdraw Consent',
    declinedTitle: 'Consent Declined',
    declinedDesc: 'You have declined the consent form. You must sign the consent form to participate in the clinical trial.\nPlease contact the study coordinator if you wish to participate.',
    goBack: 'Go Back',
    declineConfirmTitle: 'Confirm Decline',
    declineConfirmDesc: 'If you decline, you will not be able to participate in this clinical trial. Are you sure?',
    declineConfirmYes: 'Yes, Decline',
    declineConfirmCancel: 'Go Back',
    // Withdraw confirmation modal
    withdrawConfirmTitle: 'Confirm Withdrawal',
    withdrawConfirmDesc: 'If you withdraw, you will no longer be able to participate in this clinical trial. Are you sure?',
    withdrawConfirmYes: 'Yes, Withdraw',
    withdrawConfirmCancel: 'Cancel',
  },
};

// =============================================================================
// PAGE: TRAINING LIST
// =============================================================================

export const PAGE_TRAINING_LIST = {
  ko: {
    title: '설문 연습 현황',
    loading: '불러오는 중...',
    errorLoading: '데이터를 불러오지 못했습니다.',
    empty: '설문 연습이 있는 프로젝트가 없습니다.',
    readonly: '수정 불가',
    completionCount: (count: number) => `완료 ${count}회`,
  },
  en: {
    title: 'Survey Training Status',
    loading: 'Loading...',
    errorLoading: 'Failed to load data.',
    empty: 'No projects with survey training.',
    readonly: 'Read only',
    completionCount: (count: number) => `${count} completion${count > 1 ? 's' : ''}`,
  },
};

// =============================================================================
// PAGE: TRAINING PAGE ROUTE
// =============================================================================

export const PAGE_TRAINING = {
  ko: {
    pageTitle: '연습 설문',
    backLabel: '뒤로 가기',
    loading: '트레이닝 정보를 불러오는 중...',
    retry: '다시 시도',
    pendingSectionTitle: '진행할 연습',
    completedSectionTitle: '완료된 연습',
    completedDefault: '연습 완료',
    noPendingHint: '진행 가능한 트레이닝이 없습니다. 연구팀에 문의하세요.',
    successText: '연습이 완료되었습니다. 아래 버튼을 눌러 확인해 주세요.',
    footerHint: '연습 설문을 하나 이상 완료하면 버튼이 활성화됩니다.',
    confirmButton: '훈련 완료',
    confirming: '처리 중...',
  },
  en: {
    pageTitle: 'Training Survey',
    backLabel: 'Go back',
    loading: 'Loading training information...',
    retry: 'Retry',
    pendingSectionTitle: 'Pending Training',
    completedSectionTitle: 'Completed Training',
    completedDefault: 'Training completed',
    noPendingHint: 'No available training. Please contact the study team.',
    successText: 'Training is complete. Please tap the button below to confirm.',
    footerHint: 'Complete at least one training survey to enable the button.',
    confirmButton: 'Complete Training',
    confirming: 'Processing...',
  },
};

// =============================================================================
// CONSTANTS: PROJECT STATUS (다국어)
// =============================================================================

export const PROJECT_STATUS_LABELS = {
  ko: {
    ACTIVE: '',
    DRAFT: '준비 중',
    PAUSED: '일시 중단',
    COMPLETED: '완료',
    CANCELLED: '취소',
  },
  en: {
    ACTIVE: '',
    DRAFT: 'Preparing',
    PAUSED: 'Paused',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
  },
};

export const CONSENT_STATUS_BADGES = {
  ko: {
    signed:   { text: '동의 완료', color: '#15803d', bg: '#dcfce7' },
    unsigned: { text: '미동의',   color: '#b45309', bg: '#fef9c3' },
    declined: { text: '동의 거부', color: '#991b1b', bg: '#fee2e2' },
  },
  en: {
    signed:   { text: 'Signed',   color: '#15803d', bg: '#dcfce7' },
    unsigned: { text: 'Unsigned', color: '#b45309', bg: '#fef9c3' },
    declined: { text: 'Declined', color: '#991b1b', bg: '#fee2e2' },
  },
};

export const TRAINING_STATUS_BADGES = {
  ko: {
    passed:  { text: '통과', color: '#15803d', bg: '#dcfce7' },
    pending: { text: '미완료', color: '#b45309', bg: '#fef9c3' },
  },
  en: {
    passed:  { text: 'Passed',  color: '#15803d', bg: '#dcfce7' },
    pending: { text: 'Pending', color: '#b45309', bg: '#fef9c3' },
  },
};

// =============================================================================
// COMPONENT: SYNC STATUS INDICATOR
// =============================================================================

export const COMPONENT_SYNC_STATUS = {
  ko: {
    offline: '오프라인',
    offlineTitle: '잠시 연결이 끊어졌어요',
    offlineSavedMessage: '작성 중이던 내용은 안전하게 저장되어 있어요.',
    offlineAutoMessage: '인터넷이 다시 연결되면 자동으로 진행돼요.',
    reconnect: '다시 시도하기',
    reconnecting: '확인하는 중...',
    reconnectFailed: '아직 연결되지 않아요. 잠시 후 다시 시도해 주세요.',
    submitFailedTitle: '제출을 완료하지 못했어요',
    submitFailedSavedMessage: '입력하신 내용은 그대로 저장되어 있어요.',
    submitFailedHint: '네트워크 상태를 확인한 뒤 다시 시도해 주세요.',
    submitRetry: '다시 제출하기',
    submitRetrying: '제출 중...',
    submitRetryFailed: '아직 제출되지 않았어요. 잠시 후 다시 시도해 주세요.',
    syncing: '동기화 중...',
    syncNeeded: (count: number) => `동기화 필요 (${count})`,
    syncNow: '지금 동기화',
    connectionWifi: 'Wi-Fi',
    connectionMobile: '모바일 데이터',
    connectionLabel: (type: string) => `연결: ${type}`,
    lastSync: (time: string) => `마지막 동기화: ${time}`,
    justNow: '방금 전',
    minutesAgo: (n: number) => `${n}분 전`,
    hoursAgo: (n: number) => `${n}시간 전`,
    daysAgo: (n: number) => `${n}일 전`,
  },
  en: {
    offline: 'Offline',
    offlineTitle: 'Connection paused',
    offlineSavedMessage: 'Your progress is safely saved.',
    offlineAutoMessage: "We'll reconnect automatically when your internet is back.",
    reconnect: 'Try again',
    reconnecting: 'Checking...',
    reconnectFailed: 'Still no connection. Please try again in a moment.',
    submitFailedTitle: "Couldn't send your submission",
    submitFailedSavedMessage: 'Your answers are kept safe on this device.',
    submitFailedHint: 'Please check your connection and try again.',
    submitRetry: 'Submit again',
    submitRetrying: 'Submitting...',
    submitRetryFailed: 'Still couldn\u2019t send. Please try again in a moment.',
    syncing: 'Syncing...',
    syncNeeded: (count: number) => `Sync needed (${count})`,
    syncNow: 'Sync now',
    connectionWifi: 'Wi-Fi',
    connectionMobile: 'Mobile data',
    connectionLabel: (type: string) => `Connection: ${type}`,
    lastSync: (time: string) => `Last sync: ${time}`,
    justNow: 'Just now',
    minutesAgo: (n: number) => `${n}m ago`,
    hoursAgo: (n: number) => `${n}h ago`,
    daysAgo: (n: number) => `${n}d ago`,
  },
};

// =============================================================================
// COMPONENT: TRAINING GATE
// =============================================================================

export const COMPONENT_TRAINING_GATE = {
  ko: {
    checkingStatus: '트레이닝 상태 확인 중...',
    errorTitle: '오류 발생',
    errorCheck: '트레이닝 상태를 확인할 수 없습니다.',
    title: '트레이닝 필수',
    message: '설문에 참여하기 전에 트레이닝을 완료해야 합니다.',
    instructionsTitle: '트레이닝 안내',
    instructionPurpose: '설문 작성 방법을 익히고 시스템 사용법을 배웁니다',
    instructionDuration: '약 5-10분',
    instructionImportant: '트레이닝 답변은 연구 데이터에 포함되지 않습니다',
    instructionAfter: '자동으로 설문에 접근할 수 있습니다',
    labelPurpose: '목적:',
    labelDuration: '소요 시간:',
    labelImportant: '중요:',
    labelAfter: '완료 후:',
    startButton: '트레이닝 시작',
    complianceNote: '임상시험 규정에 따라 모든 참가자는 트레이닝을 완료해야 합니다.',
  },
  en: {
    checkingStatus: 'Checking training status...',
    errorTitle: 'Error',
    errorCheck: 'Unable to check training status.',
    title: 'Training Required',
    message: 'You must complete training before participating in the survey.',
    instructionsTitle: 'Training Guide',
    instructionPurpose: 'Learn how to complete surveys and use the system',
    instructionDuration: 'Approx. 5-10 minutes',
    instructionImportant: 'Training responses are not included in study data',
    instructionAfter: 'You will automatically gain access to surveys',
    labelPurpose: 'Purpose:',
    labelDuration: 'Duration:',
    labelImportant: 'Important:',
    labelAfter: 'After completion:',
    startButton: 'Start Training',
    complianceNote: 'Per clinical trial regulations, all participants must complete training.',
  },
};

// =============================================================================
// PAGE: HISTORY DETAIL
// =============================================================================

export const PAGE_HISTORY_DETAIL = {
  ko: {
    notFound: '설문 이력을 찾을 수 없습니다.',
    goBackToList: '목록으로 돌아가기',
    backLabel: '뒤로 가기',
    submittedAt: (date: string) => `제출일: ${date}`,
    emptyAnswers: '답변 내역이 없습니다.',
    questionFallback: (index: number) => `질문 ${index}`,
    noAnswer: '(답변 없음)',
  },
  en: {
    notFound: 'Survey history not found.',
    goBackToList: 'Back to list',
    backLabel: 'Go back',
    submittedAt: (date: string) => `Submitted: ${date}`,
    emptyAnswers: 'No answers available.',
    questionFallback: (index: number) => `Question ${index}`,
    noAnswer: '(No answer)',
  },
};

// =============================================================================
// HOOKS: ERROR MESSAGES (shared)
// =============================================================================

export const HOOK_ERROR_MESSAGES = {
  ko: {
    failedToStartSurvey: '설문을 시작하지 못했습니다',
    cannotEditSurvey: '이 설문은 현재 수정할 수 없습니다.',
    failedToLoadHistory: '이력을 불러오지 못했습니다',
    trainingInfoInvalid: '훈련 설문 정보가 올바르지 않습니다.',
    trainingLoadFailed: '훈련 설문을 불러오지 못했습니다. 다시 시도해 주세요.',
    trainingInfoMissing: '훈련 정보가 누락되었습니다.',
    patientFallbackName: '환자',
    questionFallback: '질문',
    surveyFallbackTitle: '설문',
    // consent page route
    missingProjectId: '프로젝트 ID가 없습니다. 세션 목록으로 돌아가세요.',
    consentLoadFailed: '동의서를 불러오지 못했습니다.',
    consentLoadFailedRetry: '동의서를 불러오지 못했습니다. 다시 시도해 주세요.',
    consentWithdrawReason: '동의 철회',
    // training page route
    trainingRouteLoadFailed: '트레이닝 정보를 불러오지 못했습니다.',
    trainingRouteLoadFailedRetry: '트레이닝 정보를 불러오지 못했습니다. 다시 시도해 주세요.',
    // session page data
    errorLoadingSessions: '세션 정보를 불러오지 못했습니다',
  },
  en: {
    failedToStartSurvey: 'Failed to start survey',
    cannotEditSurvey: 'This survey cannot be edited at this time.',
    failedToLoadHistory: 'Failed to load history',
    trainingInfoInvalid: 'Training survey information is invalid.',
    trainingLoadFailed: 'Failed to load training survey. Please try again.',
    trainingInfoMissing: 'Training information is missing.',
    patientFallbackName: 'Patient',
    questionFallback: 'Question',
    surveyFallbackTitle: 'Survey',
    // consent page route
    missingProjectId: 'Project ID is missing. Please return to the session list.',
    consentLoadFailed: 'Failed to load consent.',
    consentLoadFailedRetry: 'Failed to load consent. Please try again.',
    consentWithdrawReason: 'Consent withdrawn',
    // training page route
    trainingRouteLoadFailed: 'Failed to load training information.',
    trainingRouteLoadFailedRetry: 'Failed to load training information. Please try again.',
    // session page data
    errorLoadingSessions: 'Failed to load session information',
  },
};

// =============================================================================
// BIRTH DATE STEP: VALIDATION
// =============================================================================

export const BIRTH_DATE_VALIDATION = {
  ko: {
    yearRequired: '연도 4자리를 입력해주세요.',
    monthRequired: '월 2자리를 입력해주세요.',
    dayRequired: '일 2자리를 입력해주세요.',
    invalidMonth: '올바른 월을 입력해주세요. (01~12)',
    invalidDay: '올바른 일을 입력해주세요. (01~31)',
    yearLabel: '년',
    monthLabel: '월',
    dayLabel: '일',
  },
  en: {
    yearRequired: 'Please enter a 4-digit year.',
    monthRequired: 'Please enter a 2-digit month.',
    dayRequired: 'Please enter a 2-digit day.',
    invalidMonth: 'Please enter a valid month (01-12).',
    invalidDay: 'Please enter a valid day (01-31).',
    yearLabel: 'Year',
    monthLabel: 'Month',
    dayLabel: 'Day',
  },
};

// =============================================================================
// COMPONENT: TRAINING REQUIRED BANNER
// =============================================================================

export const COMPONENT_TRAINING_BANNER = {
  ko: {
    title: '설문 연습을 먼저 완료해주세요',
    description: '실제 설문 참여 전 연습 설문을 완료해야 합니다. 아래 설문은 연습 완료 후 참여할 수 있습니다.',
    button: '연습 시작',
  },
  en: {
    title: 'Please complete training first',
    description: 'You must complete the training survey before participating. Surveys below will be available after completing training.',
    button: 'Start Training',
  },
};

// =============================================================================
// PAGE: HISTORY (additional labels)
// =============================================================================

export const PAGE_HISTORY_EXTRA = {
  ko: {
    scoreTrend: '점수 추이',
    completedSurveyCount: (count: number) => `완료된 설문: ${count}개`,
    completedTests: '완료한 검사',
  },
  en: {
    scoreTrend: 'Score Trends',
    completedSurveyCount: (count: number) => `Completed surveys: ${count}`,
    completedTests: 'Completed Tests',
  },
};

// =============================================================================
// PAGE: COMPLETE (additional labels)
// =============================================================================

export const PAGE_COMPLETE_EXTRA = {
  ko: {
    nextSurveyButton: '다음 설문으로 이동',
  },
  en: {
    nextSurveyButton: 'Go to Next Survey',
  },
};

// =============================================================================
// PAGE: TOKEN SESSION LIST (additional fallbacks)
// =============================================================================

export const PAGE_TOKEN_SESSION_LIST_EXTRA = {
  ko: {
    loadingFallback: '로딩 중...',
    noSessionInfo: '세션 정보가 없습니다',
    noSurveys: '참여 가능한 설문이 없습니다',
  },
  en: {
    loadingFallback: 'Loading...',
    noSessionInfo: 'No session information available',
    noSurveys: 'No available surveys',
  },
};

// =============================================================================
// VALIDATION: ZOD SCHEMAS
// =============================================================================

export const VALIDATION_SCHEMA_MESSAGES = {
  ko: {
    activationCodeRequired: '인증코드를 입력해주세요',
    activationCodeLength: '인증코드 길이가 올바르지 않습니다',
    activationCodeFormat: '올바른 형식이 아닙니다',
    birthDateRequired: '생년월일을 입력해주세요',
    birthDateDigitsOnly: '숫자만 입력 가능합니다',
    sessionRequired: '설문을 선택해주세요',
  },
  en: {
    activationCodeRequired: 'Please enter the activation code',
    activationCodeLength: 'Activation code length is invalid',
    activationCodeFormat: 'Invalid format',
    birthDateRequired: 'Please enter your date of birth',
    birthDateDigitsOnly: 'Only digits are allowed',
    sessionRequired: 'Please select a survey',
  },
};

// =============================================================================
// TROUBLESHOOTING: FORMS NOT APPEARING
// =============================================================================

export const TROUBLESHOOTING_FORMS = {
  ko: {
    title: '설문이 보이지 않나요?',
    subtitle: '자동 진단을 통해 문제를 확인하고 해결 방법을 안내합니다.',
    allPassMessage: '모든 검사 통과! 시스템이 정상입니다.',
    issueCount: (fail: number) => `${fail}개 문제`,
    warningCount: (warn: number) => `${warn}개 경고`,
    found: '발견',
    solutionLabel: '해결방법:',
    retryDiagnostics: '다시 진단',
    syncing: '동기화 중...',
    syncNow: '지금 동기화',
    helpTitle: '그래도 문제가 해결되지 않나요?',
    helpSubtitle: '다음 사항을 확인해주세요:',
    helpItems: [
      '설문 시작 시간이 되었는지 확인 (일부 설문은 특정 시간에만 접근 가능)',
      '이전에 이미 완료한 설문인지 확인',
      '앱을 완전히 종료한 후 다시 실행',
      '기기를 재시작',
    ],
    // Diagnostic titles & messages
    networkTitle: '네트워크 연결',
    networkPass: (type: string) => `인터넷에 연결되어 있습니다 (${type})`,
    networkFail: '인터넷 연결이 없습니다',
    networkSolution: 'Wi-Fi 또는 모바일 데이터를 켜고 다시 시도해주세요.',
    connectionWifi: 'Wi-Fi',
    connectionMobile: '모바일 데이터',
    connectionGeneric: '연결됨',
    storageTitle: '기기 저장소',
    storagePass: '기기 저장소가 정상 작동합니다',
    storageFail: '기기 저장소에 접근할 수 없습니다',
    storageSolution: '브라우저 설정에서 쿠키 및 사이트 데이터를 허용해주세요.',
    browserTitle: '브라우저 호환성',
    browserPass: '브라우저가 호환됩니다',
    browserFail: '브라우저가 오래되었습니다',
    browserSolution: '최신 버전의 Chrome, Safari, 또는 Firefox를 사용해주세요.',
    timeTitle: '기기 시간',
    timePass: (time: string) => `기기 시간이 정확합니다 (${time})`,
    timeFail: '기기 시간이 정확하지 않을 수 있습니다',
    timeSolution: '기기 설정에서 "자동 날짜 및 시간"을 켜주세요.',
    syncTitle: '동기화 대기',
    syncTitlePass: '동기화 상태',
    syncPending: (count: number) => `${count}개의 제출이 동기화 대기 중입니다`,
    syncSolution: '우측 하단의 "지금 동기화" 버튼을 눌러 동기화해주세요.',
    syncPass: '모든 데이터가 동기화되었습니다',
  },
  en: {
    title: "Surveys not appearing?",
    subtitle: 'Run automatic diagnostics to identify and resolve issues.',
    allPassMessage: 'All checks passed! System is working normally.',
    issueCount: (fail: number) => `${fail} issue(s)`,
    warningCount: (warn: number) => `${warn} warning(s)`,
    found: 'found',
    solutionLabel: 'Solution:',
    retryDiagnostics: 'Run Again',
    syncing: 'Syncing...',
    syncNow: 'Sync Now',
    helpTitle: 'Still having issues?',
    helpSubtitle: 'Please check the following:',
    helpItems: [
      'Check if the survey start time has arrived (some surveys are only accessible at specific times)',
      'Check if you have already completed this survey',
      'Fully close and reopen the app',
      'Restart your device',
    ],
    networkTitle: 'Network Connection',
    networkPass: (type: string) => `Connected to the internet (${type})`,
    networkFail: 'No internet connection',
    networkSolution: 'Turn on Wi-Fi or mobile data and try again.',
    connectionWifi: 'Wi-Fi',
    connectionMobile: 'Mobile Data',
    connectionGeneric: 'Connected',
    storageTitle: 'Device Storage',
    storagePass: 'Device storage is working properly',
    storageFail: 'Cannot access device storage',
    storageSolution: 'Allow cookies and site data in browser settings.',
    browserTitle: 'Browser Compatibility',
    browserPass: 'Browser is compatible',
    browserFail: 'Browser is outdated',
    browserSolution: 'Please use the latest version of Chrome, Safari, or Firefox.',
    timeTitle: 'Device Time',
    timePass: (time: string) => `Device time is accurate (${time})`,
    timeFail: 'Device time may be inaccurate',
    timeSolution: 'Turn on "Automatic date and time" in device settings.',
    syncTitle: 'Sync Pending',
    syncTitlePass: 'Sync Status',
    syncPending: (count: number) => `${count} submission(s) pending sync`,
    syncSolution: 'Press the "Sync Now" button at the bottom right.',
    syncPass: 'All data has been synced',
  },
};

// =============================================================================
// TROUBLESHOOTING: DATA NOT TRANSFERRING
// =============================================================================

export const TROUBLESHOOTING_DATA = {
  ko: {
    title: '데이터 전송 상태',
    subtitle: '네트워크 연결과 데이터 동기화 상태를 확인합니다.',
    networkTitle: '네트워크 상태',
    online: '온라인',
    offline: '오프라인',
    connectionWifi: 'Wi-Fi',
    connectionMobile: '모바일 데이터',
    connectionGeneric: '연결됨',
    pendingTitle: '대기 중인 데이터',
    allSynced: '모든 데이터가 동기화되었습니다',
    lastSync: (time: string) => `마지막 동기화: ${time}`,
    totalPending: '총 대기 중',
    retryPending: '재시도 대기',
    retryExhausted: '재시도 초과',
    surveyResponse: '설문 응답',
    statusPending: '대기 중',
    statusSyncing: '전송 중',
    statusFailed: '재시도 예정',
    statusExhausted: '재시도 초과',
    createdAt: (time: string) => `작성: ${time}`,
    retryCount: (current: number, max: number) => `재시도: ${current}/${max}`,
    errorLabel: '오류:',
    syncing: '동기화 중...',
    syncNow: '지금 동기화',
    offlineWarning: '인터넷 연결이 필요합니다',
    helpTitle: '동기화 문제 해결',
    helpOffline: '오프라인 상태:',
    helpOfflineDesc: 'Wi-Fi 또는 모바일 데이터를 켜주세요.',
    helpWeakSignal: '약한 신호:',
    helpWeakSignalDesc: '더 나은 네트워크 환경으로 이동해주세요.',
    helpExhausted: '재시도 초과:',
    helpExhaustedDesc: '네트워크 상태를 확인하고 "지금 동기화" 버튼을 눌러주세요.',
    helpPersistent: '계속 실패:',
    helpPersistentDesc: '앱을 완전히 종료한 후 다시 실행해주세요.',
  },
  en: {
    title: 'Data Transfer Status',
    subtitle: 'Check network connection and data synchronization status.',
    networkTitle: 'Network Status',
    online: 'Online',
    offline: 'Offline',
    connectionWifi: 'Wi-Fi',
    connectionMobile: 'Mobile Data',
    connectionGeneric: 'Connected',
    pendingTitle: 'Pending Data',
    allSynced: 'All data has been synced',
    lastSync: (time: string) => `Last sync: ${time}`,
    totalPending: 'Total Pending',
    retryPending: 'Retry Pending',
    retryExhausted: 'Retry Exhausted',
    surveyResponse: 'Survey Response',
    statusPending: 'Pending',
    statusSyncing: 'Transferring',
    statusFailed: 'Retry Scheduled',
    statusExhausted: 'Retry Exhausted',
    createdAt: (time: string) => `Created: ${time}`,
    retryCount: (current: number, max: number) => `Retry: ${current}/${max}`,
    errorLabel: 'Error:',
    syncing: 'Syncing...',
    syncNow: 'Sync Now',
    offlineWarning: 'Internet connection required',
    helpTitle: 'Troubleshooting Sync Issues',
    helpOffline: 'Offline:',
    helpOfflineDesc: 'Turn on Wi-Fi or mobile data.',
    helpWeakSignal: 'Weak signal:',
    helpWeakSignalDesc: 'Move to a better network environment.',
    helpExhausted: 'Retry exhausted:',
    helpExhaustedDesc: 'Check your network and press the "Sync Now" button.',
    helpPersistent: 'Persistent failure:',
    helpPersistentDesc: 'Fully close and reopen the app.',
  },
};

// Legacy function for backward compatibility
export const getTranslation = (lang: Language) => REVIEW_SCREEN_TEXT[lang];
